/**
 * @file Contains functions for generating the HTML structure of individual
 * Kanban cards for different item types (Tasks, Patients, Projects, Assets)
 * in both "fast" (using local data) and "WIP" (using potentially fetched data) modes.
 */

// --- Imports ---

// State management
import { appData, state } from '../../app/state/state.js';
import { getResolvedCardDate, sortCanonicalTaskItems } from './itemSortUtils.js';
import {
    formatBoardCardDate,
    formatBoardCardDateCompact,
    renderBoardCardChecklistPreview,
    renderBoardCardDateSlot,
    renderBoardCardInfoRow,
    renderBoardCardShell,
    renderBoardCardStatusChip,
    renderBoardCardTypeChip,
    renderBoardCardUserSlot,
    renderParentBoardCard,
} from './boardCardView.js';
// Utility functions
import {
    findItem,
    getItemDisplayName,
    getNextActionDate,
} from "../utils/itemLookup.js";
import {
    getUserInitials,
    getUserFullDisplayName,
} from "../utils/userDisplay.js";
import {
    formatDateShort,
    formatDate,
    formatDateTime,
} from "../utils/dateUtils.js";
import {
    sanitizeHTML,
    escapeHTML,
} from "../utils/contentUtils.js";
import {
    isItemClosed,
} from "../utils/closedState.js";
import {
    getTaskDisplayUserIds,
    getTaskQuickCloseStatusConfig,
} from "../utils/taskDisplay.js";

// --- Internal Helper Functions ---

/**
 * Gets the configuration objects for a task's priority and status. (Internal helper)
 * @param {object} task - The task object.
 * @returns {{priority: object, status: object}} Objects containing priority and status config (or empty objects if not found).
 */
function getTaskBadgeInfo(task) {
    if (!task) {return { priority: {}, status: {} };}
    const priorities = appData?.config?.priorities || [];
    const taskStatuses = appData?.config?.taskStatus || [];
    const priority = priorities.find(p => p.id === task.priority) || {};
    const status = taskStatuses.find(s => s.id === task.status) || {};
    return { priority, status };
}

function getStatusConfig(itemType, item) {
    switch (itemType) {
        case 'task':
            return (appData?.config?.taskStatus || []).find(s => s.id === item.status) || {};
        case 'patient':
            return (appData?.config?.patientStatus || []).find(s => s.id === item.status) || {};
        case 'project':
            return (appData?.config?.projectStatus || []).find(s => s.id === item.status) || {};
        case 'asset':
            return (appData?.config?.equipmentStatus || []).find(s => s.id === item.equipment_status) || {};
        default:
            return {};
    }
}

function getPriorityConfig(item) {
    return (appData?.config?.priorities || []).find(p => p.id === item?.priority) || {};
}

function getItemTypesConfig(itemType) {
    switch (itemType) {
        case 'patient': return appData?.config?.patientTypes || [];
        case 'project': return appData?.config?.projectTypes || [];
        case 'asset': return appData?.config?.assetTypes || [];
        case 'task': return appData?.config?.taskTypes || [];
        case 'lab': return appData?.config?.labTypes || [];
        default: return [];
    }
}

function getLimitedInitials(initials = [], maxVisible = 3) {
    const safeInitials = (initials || []).filter(Boolean);
    if (safeInitials.length <= maxVisible) {
        return safeInitials.join(', ');
    }

    return `${safeInitials.slice(0, maxVisible).join(', ')}...`;
}

function getParentCardUserDisplay(itemType, item) {
    if (itemType === 'patient') {
        const manager = appData?.users?.find((user) => user.id === item.case_manager);
        return {
            text: manager ? getUserInitials(manager.id) : '??',
            title: manager ? getUserFullDisplayName(manager) : 'No Manager',
        };
    }

    if (itemType === 'project') {
        const managerIds = Array.isArray(item?.project_manager) ? item.project_manager : [];
        const managers = managerIds.map((id) =>
            appData?.users?.find((user) => user.id === id)
        ).filter(Boolean);

        return {
            text: getLimitedInitials(managers.map((manager) => getUserInitials(manager.id)), 3),
            title: managers.map((manager) => getUserFullDisplayName(manager)).join('\n') || 'No Managers',
        };
    }

    return {
        text: '',
        title: '',
    };
}

function getParentCardDateTooltip(itemType, item, taskList, resolvedDate) {
    if (itemType === 'patient') {
        const nextAppointment = item?.next_appointment || null;
        const nextActionDue = getNextActionDate(item?.id, taskList || []);
        const tooltipLines = [];

        if (nextAppointment) {
            tooltipLines.push('Next appointment:');
            tooltipLines.push(formatDateTime(nextAppointment));
        }

        if (nextActionDue) {
            if (tooltipLines.length > 0) {
                tooltipLines.push('');
            }
            tooltipLines.push('Next action due:');
            tooltipLines.push(formatDate(nextActionDue));
        }

        if (tooltipLines.length > 0) {
            return tooltipLines.join('\n');
        }
    }

    return resolvedDate ? formatDate(resolvedDate) : '';
}

function getPrimaryTypeChip(itemType, item, options = {}) {
    return renderBoardCardTypeChip(
        Array.isArray(item?.types) ? item.types : [],
        getItemTypesConfig(itemType),
        options
    );
}

function getStatusChip(itemType, item) {
    return renderBoardCardStatusChip(getStatusConfig(itemType, item));
}

function getTaskQuickCloseButtonHtml(task, positionClasses = 'absolute top-2 right-2 opacity-0 group-hover:opacity-100') {
    const quickCloseStatus = getTaskQuickCloseStatusConfig();
    if (!quickCloseStatus || !task?.id || isItemClosed(task)) {return '';}

    return `
        <button type="button" class="task-quick-close-btn icon-action-btn kanban-card-action-btn ${positionClasses}" data-task-id="${task.id}" title="Quick close">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
            </svg>
        </button>
    `;
}

function getTaskRecurringIndicatorHtml(task) {
    if (!task?.recurrence?.frequency) {return '';}

    return `
        <span class="absolute top-2 right-2 inline-flex h-[22px] w-[22px] items-center justify-center rounded-full text-gray-300 pointer-events-none" title="Recurring task">
            <span class="material-symbols-outlined text-[14px] leading-none">repeat</span>
        </span>
    `;
}

function getCardBorderColor(itemType, item) {
    const priority = getPriorityConfig(item);
    if (priority?.color && itemType !== 'asset') {
        return priority.color;
    }

    return getStatusConfig(itemType, item).color || '#e5e7eb';
}

function getSortedOpenParentTasks(parentId, tasks) {
    const openTasks = (tasks || []).filter(task =>
        task.parentId === parentId &&
        !isItemClosed(task)
    );

    return sortCanonicalTaskItems(openTasks, tasks || appData?.tasks || []);
}

function getTaskUserTooltip(task) {
    if (!task) {return 'None';}

    const ownerName = task.owner ?
        getUserFullDisplayName(
            appData?.users?.find((entry) => entry.id === task.owner)
        ) :
        '';
    const assignedNames = Array.isArray(task.assigned_users) ?
        task.assigned_users
            .map((userId) => getUserFullDisplayName(
                appData?.users?.find((entry) => entry.id === userId)
            ))
            .filter(Boolean) :
        [];

    if (ownerName && assignedNames.length > 0) {
        return [
            'Owner:',
            ownerName,
            '',
            'Assigned:',
            ...assignedNames,
        ].join('\n');
    }

    if (ownerName) {
        return [
            'Owner:',
            ownerName,
        ].join('\n');
    }

    if (assignedNames.length > 0) {
        return [
            'Assigned:',
            ...assignedNames,
        ].join('\n');
    }

    return 'None';
}

function getTaskUserDisplayText(task, maxVisible = 1) {
    if (!task) {return '';}

    const userIds = getTaskDisplayUserIds(task);

    if (userIds.length === 0) {return '';}

    const uniqueInitials = Array.from(new Set(
        userIds.map((userId) => getUserInitials(userId)).filter(Boolean)
    ));

    if (uniqueInitials.length <= maxVisible) {
        return uniqueInitials.join(', ');
    }

    return `${uniqueInitials.slice(0, maxVisible).join(', ')}...`;
}

function getTaskTypesLabel(task) {
    const taskTypeConfig = getItemTypesConfig('task');
    const typeIds = Array.isArray(task?.types) ? task.types : [];
    if (typeIds.length === 0) {return 'None';}

    return typeIds.map((typeId) =>
        taskTypeConfig.find((type) => type.id === typeId)?.name || typeId
    ).join('\n');
}

function getLabTypeLabel(lab) {
    const labTypeConfig = getItemTypesConfig('lab');
    const typeIds = Array.isArray(lab?.types) ? lab.types : [];
    const firstTypeId = typeIds[0] || '';
    return labTypeConfig.find((type) => type.id === firstTypeId)?.name ||
        firstTypeId ||
        'Lab type missing';
}

function getLabLocationLabel(lab) {
    const labId = lab?.lab || '';
    if (labId === 'internal') {
        return appData?.config?.labs?.internalLabLabel || 'Internal lab';
    }
    return (appData?.config?.labs?.externalLabs || [])
        .find((entry) => entry?.id === labId)?.name ||
        labId ||
        'Lab not set';
}

function getSortedOpenPatientLabs(patientId) {
    if (!patientId) {return [];}

    return (appData?.labs || [])
        .filter((lab) => lab?.linked_patient_id === patientId && !isItemClosed(lab))
        .sort((labA, labB) => {
            const dateA = getResolvedCardDate('lab', labA, appData?.tasks || []);
            const dateB = getResolvedCardDate('lab', labB, appData?.tasks || []);
            const parsedDateA = dateA ? new Date(dateA) : null;
            const parsedDateB = dateB ? new Date(dateB) : null;
            if (parsedDateA && parsedDateB && parsedDateA.getTime() !== parsedDateB.getTime()) {
                return parsedDateA - parsedDateB;
            }
            if (parsedDateA) {return -1;}
            if (parsedDateB) {return 1;}
            return getLabTypeLabel(labA).localeCompare(getLabTypeLabel(labB));
        });
}

function renderPatientLabListHtml(patient, {limit = null} = {}) {
    const visibleLabs = getSortedOpenPatientLabs(patient?.id);
    const labs = limit ? visibleLabs.slice(0, limit) : visibleLabs;
    const priorities = appData?.config?.priorities || [];
    const statuses = appData?.config?.labStatus || [];

    return labs.map((lab) => {
        const priority = priorities.find((entry) => entry.id === lab.priority) || {};
        const status = statuses.find((entry) => entry.id === lab.status) || {};
        const priorityColor = priority.color || '#d1d5db';
        const displayDate = getResolvedCardDate('lab', lab, appData?.tasks || []);
        const displayDateFormatted = formatDateShort(displayDate);
        const displayDateTooltip = formatDate(displayDate);
        const title = getLabTypeLabel(lab);
        const labTooltip = [
            title,
            `Lab: ${getLabLocationLabel(lab)}`,
            `Status: ${status.name || 'N/A'}`,
        ].join('\n');

        return `
            <div class="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 w-full">
                <span class="block h-2.5 w-2.5 rounded-full flex-shrink-0" style="background-color: ${priorityColor};" title="Urgency: ${escapeHTML(priority.name || 'N/A')}"></span>
                <span class="flex-grow truncate" title="${escapeHTML(labTooltip)}">${escapeHTML(title)}</span>
                <span class="flex-shrink-0" title="${escapeHTML(displayDateTooltip || '')}">${displayDateFormatted}</span>
            </div>
        `;
    }).join('');
}

/**
 * Renders compact task summary rows for embedded parent-card task lists.
 * @param {Array<object>} tasks Task records to render.
 * @param {object} options Rendering options.
 * @param {boolean} [options.showInitials] Whether to show assignee initials.
 * @param {number|null} [options.limit] Maximum visible rows, or null for all.
 * @returns {string} Task summary row HTML.
 */
export function renderTaskListHtml(tasks, { showInitials = true, limit = null } = {}) {
    const visibleTasks = limit ? tasks.slice(0, limit) : tasks;

    return visibleTasks.map(task => {
        const { priority, status: taskStatus } = getTaskBadgeInfo(task);
        const priorityColor = priority.color || '#e5e7eb';
        const taskDueDateFormatted = formatDateShort(task.due_date);
        const taskDueDateTooltip = formatDate(task.due_date);
        const initials = showInitials ? getTaskUserDisplayText(task, 1) : '';
        const userTooltip = showInitials ? getTaskUserTooltip(task) : '';
        const taskNameTooltip = [
            task.title || 'Untitled Task',
            `Type(s): ${getTaskTypesLabel(task)}`,
            `Status: ${taskStatus.name || 'N/A'}`,
        ].join('\n');

        return `
            <div class="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 w-full">
                <span class="block h-2.5 w-2.5 rounded-full flex-shrink-0" style="background-color: ${priorityColor};" title="Urgency: ${priority.name || 'N/A'}"></span>
                <span class="flex-grow truncate" title="${escapeHTML(taskNameTooltip)}">${escapeHTML(task.title || '')}</span>
                <span class="flex-shrink-0" title="${escapeHTML(taskDueDateTooltip || '')}">${taskDueDateFormatted}</span>
                ${showInitials ? `<span class="flex-shrink-0 w-12 text-right" title="${escapeHTML(userTooltip)}">${initials}</span>` : ''}
            </div>
        `;
    }).join('');
}

function getMaintenanceTypeLabel(item) {
    const maintenanceTypes = appData?.config?.maintenanceTypes || [];
    const labels = (Array.isArray(item?.types) ? item.types : [])
        .map((typeId) =>
            maintenanceTypes.find((type) => type.id === typeId)?.name || typeId,
        )
        .filter(Boolean);
    return labels[0] || item?.item || 'Maintenance';
}

function getOpenMaintenanceForAsset(asset) {
    const priorities = appData?.config?.priorities || [];
    const priorityOrderMap = new Map(priorities.map((priority) => [priority.id, parseInt(priority.order, 10) || 0]));
    const getPriorityOrder = (id) => priorityOrderMap.get(id) ?? 999;
    const streamOrder = {
        issue: 0,
        service: 1,
    };

    return (appData?.maintenance || [])
        .filter((item) => item?.linked_asset_id === asset?.id && !isItemClosed(item))
        .sort((a, b) => {
            const streamDiff = (streamOrder[a?.stream] ?? 99) -
                (streamOrder[b?.stream] ?? 99);
            if (streamDiff !== 0) {return streamDiff;}
            const priorityDiff = getPriorityOrder(a.priority) - getPriorityOrder(b.priority);
            if (priorityDiff !== 0) {return priorityDiff;}
            const dateA = getResolvedCardDate('maintenance', a, appData?.tasks || []);
            const dateB = getResolvedCardDate('maintenance', b, appData?.tasks || []);
            const parsedDateA = dateA ? new Date(dateA) : null;
            const parsedDateB = dateB ? new Date(dateB) : null;
            if (!parsedDateA && !parsedDateB) {return 0;}
            if (!parsedDateA) {return 1;}
            if (!parsedDateB) {return -1;}
            return parsedDateA - parsedDateB;
        });
}

function renderAssetMaintenanceListHtml(asset) {
    const priorities = appData?.config?.priorities || [];
    const openMaintenance = getOpenMaintenanceForAsset(asset);

    return openMaintenance.map((item) => {
        const priority = priorities.find((entry) => entry.id === item.priority) || {};
        const priorityColor = priority.color || '#e5e7eb';
        const initials = item.managed_by ? getUserInitials(item.managed_by) : '';
        const displayDate = getResolvedCardDate('maintenance', item, appData?.tasks || []);
        const displayDateFormatted = formatDateShort(displayDate);
        const streamLabel = item.stream === 'issue' ? 'Issue' : 'Service';
        const streamIcon = item.stream === 'issue' ? 'report' : 'build_circle';
        const streamOutlineColor = item.stream === 'issue' ? '#d65f5f' : '#4599e8';
        const streamFillColor = item.stream === 'issue' ? '#fde3e3' : '#e4f2ff';
        const title = item.item || getMaintenanceTypeLabel(item);

        return `
            <div class="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 w-full" title="${escapeHTML(streamLabel)}: ${escapeHTML(title)}">
                <span class="block h-2.5 w-2.5 rounded-full flex-shrink-0" style="background-color: ${priorityColor};" title="Urgency: ${escapeHTML(priority.name || 'N/A')}"></span>
                <span class="maintenance-stream-icon-colored flex-shrink-0 text-[15px] leading-none" style="--maintenance-stream-outline: ${streamOutlineColor}; --maintenance-stream-fill: ${streamFillColor};" title="${escapeHTML(streamLabel)}">
                    <span class="maintenance-stream-icon-colored__fill material-symbols-outlined" aria-hidden="true">${streamIcon}</span>
                    <span class="maintenance-stream-icon-colored__outline material-symbols-outlined">${streamIcon}</span>
                </span>
                <span class="flex-grow truncate">${escapeHTML(title)}</span>
                <span class="flex-shrink-0" title="Date: ${escapeHTML(displayDate || 'None')}">${displayDateFormatted}</span>
                <span class="flex-shrink-0 w-12 text-right" title="Managed by: ${escapeHTML(initials || 'None')}">${escapeHTML(initials)}</span>
            </div>
        `;
    }).join('');
}

function createParentCard(itemType, item, openTasks, {
    mode = 'standard',
    quickAddDatasetKey,
} = {}) {
    if (!item?.id) {
        return '<div class="kanban-card bg-red-100 p-3 rounded-md border-l-4 border-red-500 text-xs">Invalid Card Data</div>';
    }

    const _status = getStatusConfig(itemType, item);
    const borderColor = getCardBorderColor(itemType, item);
    const userDisplay = getParentCardUserDisplay(itemType, item);
    const displayDate = getResolvedCardDate(itemType, item, openTasks);
    const displayDateFormatted = formatBoardCardDate(displayDate);
    const displayDateCompact = formatBoardCardDateCompact(displayDate);
    const displayDateTooltip = getParentCardDateTooltip(itemType, item, openTasks, displayDate);
    const sortedOpenTasks = sortCanonicalTaskItems(openTasks || [], openTasks || []);
    const taskLimit = mode === 'wip' ? null : 3;
    const labLimit = mode === 'wip' ? 5 : 3;
    const renderedTasksHtml = renderTaskListHtml(sortedOpenTasks, {
        showInitials: true,
        limit: taskLimit,
    });
    const checklistHtml = renderBoardCardChecklistPreview(item, itemType, {
        limit: mode === 'wip' ? 3 : 1,
    });
    const statusChipHtml = getStatusChip(itemType, item);
    const showUserSlot = itemType !== 'asset';
    const isNotificationsPinnedOpen = !!(
        state.isNotificationShadeOpen &&
        state.viewSettings?.notificationsPinned
    );
    const typeBudget = isNotificationsPinnedOpen ?
        (displayDateFormatted ? 14 : 46) :
        (displayDateFormatted ? 22 : 54);
    const typeChipHtml = getPrimaryTypeChip(itemType, item, {
        expandToLane: !displayDateFormatted,
        maxEstimatedUnits: typeBudget,
    });

    let embeddedContentHtml = '';

    if (itemType === 'asset') {
        const maintenanceListHtml = renderAssetMaintenanceListHtml(item);
        const hasOpenMaintenance = maintenanceListHtml.length > 0;
        const hasOpenTasks = renderedTasksHtml.length > 0;

        embeddedContentHtml = `
            ${hasOpenMaintenance ? `<div class="pt-1"><div class="space-y-1">${maintenanceListHtml}</div></div>` : ''}
            ${hasOpenTasks ? `<div class="${hasOpenMaintenance ? 'mt-2 pt-2 border-t dark:border-gray-600' : 'pt-1'}"><div class="space-y-1">${renderedTasksHtml}</div></div>` : ''}
        `;
    } else if (itemType === 'patient') {
        const labsListHtml = renderPatientLabListHtml(item, {limit: labLimit});
        const hasOpenLabs = labsListHtml.length > 0;
        const hasOpenTasks = renderedTasksHtml.length > 0;

        embeddedContentHtml = `
            ${hasOpenLabs ? `<div class="pt-1"><div class="space-y-1">${labsListHtml}</div></div>` : ''}
            ${hasOpenTasks ? `<div class="${hasOpenLabs ? 'mt-2 pt-2 border-t dark:border-gray-600' : 'pt-1'}"><div class="space-y-1">${renderedTasksHtml}</div></div>` : ''}
        `;
    } else if (renderedTasksHtml) {
        embeddedContentHtml = `<div class="space-y-1 pt-1">${renderedTasksHtml}</div>`;
    }

    return renderParentBoardCard({
        itemId: item.id,
        itemType,
        titleText: item.name || '',
        borderColor,
        statusChipHtml,
        typeChipHtml,
        dateText: displayDateFormatted,
        compactDateText: displayDateCompact,
        dateTooltip: displayDateTooltip,
        userDisplay,
        showUserSlot,
        embeddedContentHtml,
        checklistHtml,
        actionButtonHtml: !isItemClosed(item) ? `<button type="button" class="quick-add-task-btn kanban-card-action-btn absolute top-2 right-2 opacity-0 group-hover:opacity-100" data-${quickAddDatasetKey}="${item.id}" title="Add Task">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" /></svg>
            </button>` : '',
    });
}

// --- Exported Card Rendering Functions ---

/**
 * Generates the HTML for the next unchecked checklist item to display on the Kanban card.
 */
export function getChecklistHtmlForCard(item, itemType, options = {}) {
    return renderBoardCardChecklistPreview(item, itemType, options);
}

/**
 * Creates the HTML for a single Task Kanban card.
 * @param {object} task - The task object.
 * @returns {string} The HTML string for the card.
 */
export function createTaskCard(task) {
    if (!task || !task.id) {return '<div class="kanban-card bg-red-100 p-3 rounded-md border-l-4 border-red-500 text-xs">Invalid Task Data</div>';}

    const parent = task.parentId ? findItem(task.parentId) : null;
    const borderColor = getCardBorderColor('task', task);
    const displayDateFormatted = formatBoardCardDate(task.due_date);
    const displayDateCompact = formatBoardCardDateCompact(task.due_date);
    const displayDateTooltip = formatDate(task.due_date);
    const userDisplayText = getTaskUserDisplayText(task, 3);
    const userTooltip = getTaskUserTooltip(task);
    const statusChipHtml = getStatusChip('task', { status: task.status });
    const isNotificationsPinnedOpen = !!(
        state.isNotificationShadeOpen &&
        state.viewSettings?.notificationsPinned
    );
    const typeBudget = isNotificationsPinnedOpen ?
        (displayDateFormatted ? 14 : 46) :
        (displayDateFormatted ? 22 : 54);
    const typeChipHtml = getPrimaryTypeChip('task', task, {
        expandToLane: !displayDateFormatted,
        maxEstimatedUnits: typeBudget,
    });
    const checklistHtml = getChecklistHtmlForCard(task, 'task');
    const infoRowHtml = renderBoardCardInfoRow({
        statusChipHtml,
        typeChipHtml,
        dateHtml: renderBoardCardDateSlot({
            dateText: displayDateFormatted,
            compactDateText: displayDateCompact,
            tooltip: displayDateTooltip,
        }),
        userHtml: renderBoardCardUserSlot({
            text: userDisplayText || '',
            title: userTooltip,
        }),
        showUserSlot: true,
        hasEmbeddedContent: false,
        infoRowGridClassOverride: displayDateFormatted ?
            'card-info-row--task-date-user' :
            '',
    });

    return renderBoardCardShell({
        itemId: task.id,
        borderColor,
        titleHtml: `<p class="font-semibold text-sm mb-1 truncate dark:text-gray-200" title="${escapeHTML(task.title || 'Untitled Task')}">${sanitizeHTML(task.title || 'Untitled Task')}</p>`,
        secondaryTitleHtml: parent ? `<p class="text-xs text-gray-500 dark:text-gray-400 mb-2 truncate" title="${escapeHTML(getItemDisplayName(parent, 'Linked Item'))}">${escapeHTML(getItemDisplayName(parent, 'Linked Item'))}</p>` : '<div class="mb-2"></div>',
        infoRowHtml,
        checklistHtml,
        actionButtonHtml: `${getTaskRecurringIndicatorHtml(task)}${getTaskQuickCloseButtonHtml(task)}`,
    });
}

/**
 * Creates the HTML for a Patient Kanban card (Fast Mode).
 * Displays info based on the patient data and tasks present in the local `appData.tasks`.
 * @param {object} patient - The patient object.
 * @returns {string} The HTML string for the card.
 */
export function createPatientCard_Fast(patient) {
    return createParentCard('patient', patient, getSortedOpenParentTasks(patient?.id, appData?.tasks || []), {
        mode: 'standard',
        quickAddDatasetKey: 'patient-id',
    });
}

/**
 * Creates the HTML for a Project Kanban card (Fast Mode).
 * @param {object} project - The project object.
 * @returns {string} The HTML string for the card.
 */
export function createProjectCard_Fast(project) {
    return createParentCard('project', project, getSortedOpenParentTasks(project?.id, appData?.tasks || []), {
        mode: 'standard',
        quickAddDatasetKey: 'project-id',
    });
}

/**
 * Creates the HTML for an Asset Kanban card (Fast Mode).
 * Shows open issues and the current user's open tasks for the asset.
 * @param {object} asset - The asset object.
 * @returns {string} The HTML string for the card.
 */
export function createAssetCard_Fast(asset) {
    return createParentCard('asset', asset, getSortedOpenParentTasks(asset?.id, appData?.tasks || []), {
        mode: 'standard',
        quickAddDatasetKey: 'asset-id',
    });
}

/**
 * Creates the HTML for a Patient Kanban card (WIP/Detail Mode).
 * Displays info based on the patient data and *all* fetched open tasks for that patient.
 * @param {object} patient - The patient object.
 * @param {Array<object>} allOpenTasks - The pre-fetched list of all open tasks for this patient.
 * @returns {string} The HTML string for the card.
 */
export function createPatientCard_WIP(patient, allOpenTasks) {
    return createParentCard('patient', patient, allOpenTasks || [], {
        mode: 'wip',
        quickAddDatasetKey: 'patient-id',
    });
}

/**
 * Creates the HTML for a Project Kanban card (WIP/Detail Mode).
 * @param {object} project - The project object.
 * @param {Array<object>} allOpenTasks - The pre-fetched list of all open tasks for this project.
 * @returns {string} The HTML string for the card.
 */
export function createProjectCard_WIP(project, allOpenTasks) {
    return createParentCard('project', project, allOpenTasks || [], {
        mode: 'wip',
        quickAddDatasetKey: 'project-id',
    });
}

/**
 * Creates the HTML for an Asset Kanban card (WIP/Detail Mode).
 * Shows open issues and *all* fetched open tasks for the asset.
 * @param {object} asset - The asset object.
 * @param {Array<object>} allOpenTasks - The pre-fetched list of all open tasks for this asset.
 * @returns {string} The HTML string for the card.
 */
export function createAssetCard_WIP(asset, allOpenTasks) {
    return createParentCard('asset', asset, allOpenTasks || [], {
        mode: 'wip',
        quickAddDatasetKey: 'asset-id',
    });
}
